import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import PageLayout from '../../layouts/PageLayout';
import PaginationComponent from '../../components/PaginationComponent';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';
import { useFilterParams } from '../../hooks';
import { useAuth } from '../../context/AuthProvider';
import { usePendingApprovals } from '../../context/PendingApprovals';
import { branchAPI } from '../../services/branch';
import { shiftTaskHandoverAPI } from '../../services/shiftTaskHandover';

/**
 * Bàn giao công việc cho ca sau — admin/quản lý duyệt yêu cầu của nhân viên.
 * Nhân viên gửi yêu cầu → ADMIN/MANAGER duyệt hoặc từ chối; quá 5 phút không ai xử lý thì hệ thống
 * tự duyệt (AUTO_APPROVED). Màn này còn là nơi truy vết lý do bàn giao.
 */

const PAGE_SIZE = 20;
/** Chỉ tự làm mới khi đang xem tab "Chờ duyệt" — đó là tab có việc cần làm ngay. */
const REFRESH_MS = 30000;
const ALL_TAB = 'ALL';
const REASON_PREVIEW_LENGTH = 60;

const TABS = [
  { key: 'PENDING', label: 'Chờ duyệt' },
  { key: ALL_TAB, label: 'Tất cả' },
  { key: 'APPROVED', label: 'Đã duyệt' },
  { key: 'AUTO_APPROVED', label: 'Tự động duyệt' },
  { key: 'REJECTED', label: 'Từ chối' },
  { key: 'CANCELLED', label: 'Đã huỷ' },
];
const TAB_KEYS = TABS.map((t) => t.key);

const STATUS_BADGE = {
  PENDING: { label: 'Chờ duyệt', bg: 'warning', text: 'dark' },
  APPROVED: { label: 'Đã duyệt', bg: 'success' },
  AUTO_APPROVED: { label: 'Tự động duyệt', bg: 'info', text: 'dark' },
  REJECTED: { label: 'Từ chối', bg: 'danger' },
  CANCELLED: { label: 'Đã huỷ', bg: 'secondary' },
};

const isIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v);
const isPositiveInt = (v) => /^\d+$/.test(v) && Number(v) >= 1;

/** Tham số URL (deep-link) của màn — đúng khuôn useFilterParams. */
const FILTER_SPEC = {
  status: {
    default: 'PENDING',
    valid: (v) => TAB_KEYS.includes(v.toUpperCase()),
    parse: (v) => v.toUpperCase(),
  },
  branch_id: { default: '' },
  from: { default: '', valid: isIsoDate },
  to: { default: '', valid: isIsoDate },
  page: { default: 1, parse: Number, valid: isPositiveInt },
};

const IMAGE_URL_RE = /\.(png|jpe?g|gif|webp|heic|bmp)(\?|#|$)/i;

/** Mốc thời gian ISO → "DD/MM/YYYY HH:mm"; không phải ISO thì giữ nguyên chuỗi gốc. */
const fmtDateTime = (value) => {
  if (!value) return '—';
  const m = moment(value, moment.ISO_8601, true);
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : String(value);
};

const fmtTime = (value) => {
  if (!value) return '—';
  const m = moment(value, moment.ISO_8601, true);
  return m.isValid() ? m.format('HH:mm') : String(value);
};

const truncate = (text, max) => {
  if (!text) return '—';
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

/** Đếm ngược tới lúc hệ thống tự duyệt. Tự tick riêng để không làm render lại cả bảng mỗi giây. */
function AutoApproveCountdown({ autoApproveAt }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const target = moment(autoApproveAt, moment.ISO_8601, true);
  if (!target.isValid()) return null;

  const remainMs = target.valueOf() - now;
  if (remainMs <= 0) {
    return <small className="text-muted d-block">Đang chờ hệ thống tự duyệt…</small>;
  }

  const totalSec = Math.ceil(remainMs / 1000);
  const mm = Math.floor(totalSec / 60);
  const ss = String(totalSec % 60).padStart(2, '0');
  return (
    <small className="text-danger fw-semibold d-block">
      Còn {mm}:{ss} · tự duyệt lúc {target.format('HH:mm')}
    </small>
  );
}

/** Ảnh/tệp đính kèm: ảnh có thumbnail bấm để phóng to, tệp khác mở tab mới. */
function MediaList({ urls, onPreview }) {
  if (!urls.length) return <span className="text-muted">Không có ảnh/tệp đính kèm</span>;
  return (
    <div className="d-flex flex-wrap gap-2">
      {urls.map((url, idx) =>
        IMAGE_URL_RE.test(url) ? (
          <button
            key={url}
            type="button"
            className="p-0 border-0 bg-transparent"
            onClick={() => onPreview(url)}
            aria-label={`Phóng to ảnh ${idx + 1}`}
          >
            <img
              src={url}
              alt={`Ảnh đính kèm ${idx + 1}`}
              style={{ width: 96, height: 96, objectFit: 'cover' }}
              className="rounded border"
            />
          </button>
        ) : (
          <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary">
            Tệp {idx + 1}
          </a>
        ),
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || { label: status || '—', bg: 'secondary' };
  return <Badge bg={cfg.bg} text={cfg.text}>{cfg.label}</Badge>;
}

function DetailRow({ label, children }) {
  return (
    <Col md={6} className="mb-2">
      <div><small className="text-muted">{label}</small></div>
      <div className="fw-semibold">{children || '—'}</div>
    </Col>
  );
}

export default function ShiftHandoverRequestsPage() {
  const { user } = useAuth();
  // Badge "Bàn giao ca sau" trên sidebar phải rơi NGAY sau khi duyệt/từ chối.
  const { refresh: refreshPendingApprovals } = usePendingApprovals();
  const isAdmin = user?.role === 'ADMIN';

  const [filters, setFilters, resetFilters, { ignored, dismissIgnored }] =
    useFilterParams(FILTER_SPEC);
  const { status, branch_id: branchId, from, to, page } = filters;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [actingId, setActingId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Chỉ kết quả của lần gọi MỚI NHẤT được ghi vào state (đổi bộ lọc nhanh có thể về lộn thứ tự).
  const requestSeqRef = useRef(0);

  const fetchRows = useCallback(
    async ({ silent = false } = {}) => {
      const seq = ++requestSeqRef.current;
      if (!silent) setLoading(true);
      try {
        const res = await shiftTaskHandoverAPI.list({
          status: status === ALL_TAB ? '' : status,
          branch_id: branchId || '',
          from: from || '',
          to: to || '',
          page,
          limit: PAGE_SIZE,
        });
        if (seq !== requestSeqRef.current) return;
        setRows(Array.isArray(res?.data) ? res.data : []);
        setTotal(Number(res?.total ?? 0));
        setPendingCount(Number(res?.pending_count ?? 0));
      } catch (err) {
        if (seq === requestSeqRef.current) {
          toast.error(err?.message || 'Không tải được danh sách bàn giao');
        }
      } finally {
        if (!silent && seq === requestSeqRef.current) setLoading(false);
      }
    },
    [status, branchId, from, to, page],
  );

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Tự làm mới mỗi 30 giây, chỉ khi đang xem tab Chờ duyệt.
  useEffect(() => {
    if (status !== 'PENDING') return undefined;
    const id = setInterval(() => fetchRows({ silent: true }), REFRESH_MS);
    return () => clearInterval(id);
  }, [status, fetchRows]);

  // Lọc theo chi nhánh chỉ cần cho ADMIN; MANAGER đã bị backend ép đúng chi nhánh của mình.
  useEffect(() => {
    if (!isAdmin) return undefined;
    let alive = true;
    branchAPI
      .getActiveSimple()
      .then((res) => {
        if (alive) setBranches(Array.isArray(res) ? res : res?.data || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [isAdmin]);

  const selectedRow = rows.find((r) => r.request_id === selectedId) || null;

  const afterResolved = () => {
    fetchRows({ silent: true });
    refreshPendingApprovals();
  };

  const handleApprove = async (row) => {
    setActingId(row.request_id);
    try {
      await shiftTaskHandoverAPI.approve(row.shift_task_id, row.request_id);
      toast.success('Đã duyệt bàn giao công việc cho ca sau');
      setSelectedId(null);
      afterResolved();
    } catch (err) {
      toast.error(err?.message || 'Duyệt thất bại');
    } finally {
      setActingId(null);
    }
  };

  const openReject = (row) => {
    setRejectReason('');
    setRejectTarget(row);
  };

  const closeReject = () => {
    if (actingId) return;
    setRejectTarget(null);
    setRejectReason('');
  };

  const handleReject = async () => {
    const reason = rejectReason.trim();
    if (!rejectTarget || !reason) return;
    setActingId(rejectTarget.request_id);
    try {
      await shiftTaskHandoverAPI.reject(rejectTarget.shift_task_id, rejectTarget.request_id, reason);
      toast.success('Đã từ chối yêu cầu bàn giao');
      setRejectTarget(null);
      setRejectReason('');
      setSelectedId(null);
      afterResolved();
    } catch (err) {
      toast.error(err?.message || 'Từ chối thất bại');
    } finally {
      setActingId(null);
    }
  };

  const renderActions = (row, { inModal = false } = {}) => {
    const busy = actingId === row.request_id;
    const pending = row.status === 'PENDING';
    return (
      <div className="d-flex flex-wrap gap-1 justify-content-end">
        {!inModal && (
          <Button size="sm" variant="outline-primary" onClick={(e) => { e.stopPropagation(); setSelectedId(row.request_id); }}>
            Chi tiết
          </Button>
        )}
        {pending && (
          <>
            <Button
              size="sm"
              variant="success"
              disabled={busy}
              onClick={(e) => { e.stopPropagation(); handleApprove(row); }}
            >
              {busy ? 'Đang xử lý…' : 'Duyệt'}
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              disabled={busy}
              onClick={(e) => { e.stopPropagation(); openReject(row); }}
            >
              Từ chối
            </Button>
          </>
        )}
      </div>
    );
  };

  const renderTable = () => {
    if (loading) {
      return <div className="text-center py-4"><Spinner animation="border" /></div>;
    }
    if (!rows.length) {
      return (
        <Alert variant="light" className="mb-0 text-center">
          Không có yêu cầu bàn giao nào ở trạng thái này.
        </Alert>
      );
    }
    return (
      <div className="table-responsive">
        <Table hover className="align-middle mb-0">
          <thead>
            <tr>
              <th>Gửi lúc</th>
              <th>Nhân viên</th>
              <th>Chi nhánh</th>
              <th>Task</th>
              <th>Lý do</th>
              <th>Ca sau nhận lại lúc</th>
              <th>Trạng thái</th>
              <th>Người xử lý</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.request_id}
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedId(row.request_id)}
              >
                <td>{fmtDateTime(row.requested_at)}</td>
                <td>
                  <div className="fw-semibold">{row.held_by_name || '—'}</div>
                  {row.held_by_phone && <small className="text-muted">{row.held_by_phone}</small>}
                </td>
                <td>{row.branch_name || '—'}</td>
                <td>
                  <div>{row.task_name || '—'}</div>
                  <small className="text-muted">Ca bắt đầu {fmtTime(row.shift_start_time)}</small>
                </td>
                <td title={row.reason || ''} style={{ maxWidth: 260 }}>
                  {truncate(row.reason, REASON_PREVIEW_LENGTH)}
                </td>
                <td>{fmtDateTime(row.held_until_iso)}</td>
                <td>
                  <StatusBadge status={row.status} />
                  {row.status === 'PENDING' && <AutoApproveCountdown autoApproveAt={row.auto_approve_at} />}
                </td>
                <td>
                  {row.resolved_by_name || (row.status === 'AUTO_APPROVED' ? 'Hệ thống' : '—')}
                  {row.resolved_at && (
                    <small className="text-muted d-block">{fmtDateTime(row.resolved_at)}</small>
                  )}
                </td>
                <td>{renderActions(row)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderDetailBody = (row) => {
    const checklist = Object.entries(row.checklist_completed || {});
    const media = Array.isArray(row.media_urls) ? row.media_urls : [];
    return (
      <>
        <Row>
          <DetailRow label="Nhân viên gửi yêu cầu (người giữ task)">
            {row.held_by_name}{row.held_by_phone ? ` · ${row.held_by_phone}` : ''}
          </DetailRow>
          <DetailRow label="Nhân viên ca gốc">{row.employee_name}</DetailRow>
          <DetailRow label="Chi nhánh">{row.branch_name}</DetailRow>
          <DetailRow label="Ca bắt đầu">{fmtTime(row.shift_start_time)}</DetailRow>
          <DetailRow label="Gửi lúc">{fmtDateTime(row.requested_at)}</DetailRow>
          <DetailRow label="Ca sau nhận lại lúc">{fmtDateTime(row.held_until_iso)}</DetailRow>
          <DetailRow label="Hoãn (phút)">{row.postpone_minutes != null ? String(row.postpone_minutes) : null}</DetailRow>
          {row.status === 'PENDING' && (
            <DetailRow label="Tự duyệt lúc">{fmtDateTime(row.auto_approve_at)}</DetailRow>
          )}
        </Row>

        <h6 className="mt-3 mb-1">Lý do bàn giao</h6>
        <div className="border rounded p-2 mb-3" style={{ whiteSpace: 'pre-wrap' }}>
          {row.reason || '—'}
        </div>

        <h6 className="mb-1">Tiến độ checklist</h6>
        {checklist.length ? (
          <ul className="list-unstyled mb-3">
            {checklist.map(([name, done]) => (
              <li key={name} className="d-flex align-items-center gap-2 py-1">
                <Badge bg={done ? 'success' : 'secondary'}>{done ? 'Đã xong' : 'Còn lại'}</Badge>
                <span>{name}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted small mb-3">Task không có checklist.</p>
        )}

        <h6 className="mb-1">Ảnh / tệp đính kèm</h6>
        <div className="mb-3">
          <MediaList urls={media} onPreview={setPreviewUrl} />
        </div>

        <h6 className="mb-1">Xử lý</h6>
        <div className="small">
          <div>
            <StatusBadge status={row.status} />{' '}
            {row.resolved_by_name
              ? <>bởi <b>{row.resolved_by_name}</b></>
              : row.status === 'AUTO_APPROVED'
                ? <>bởi <b>Hệ thống</b></>
                : null}
            {row.resolved_at && <> lúc {fmtDateTime(row.resolved_at)}</>}
          </div>
          {row.resolve_note && (
            <div className="mt-1">Ghi chú xử lý: {row.resolve_note}</div>
          )}
        </div>
      </>
    );
  };

  const rejectReady = !!rejectReason.trim();

  return (
    <PageLayout title="Bàn giao ca sau">
      <Row>
        <Col xl={12}>
          <InvalidParamsNotice ignored={ignored} onDismiss={dismissIgnored} />
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h5 className="mb-1">Bàn giao công việc cho ca sau</h5>
                <small className="text-muted">
                  Nhân viên xin bàn giao task cho ca sau. Quá 5 phút chưa ai xử lý thì hệ thống tự duyệt.
                </small>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="d-flex flex-wrap gap-2 mb-3" role="group" aria-label="Lọc theo trạng thái">
                {TABS.map((tab) => {
                  const active = status === tab.key;
                  return (
                    <Button
                      key={tab.key}
                      size="sm"
                      variant={active ? 'primary' : 'outline-primary'}
                      aria-pressed={active}
                      onClick={() => setFilters({ status: tab.key })}
                    >
                      {tab.label}
                      {tab.key === 'PENDING' && pendingCount > 0 && (
                        <Badge bg={active ? 'light' : 'warning'} text="dark" className="ms-2">
                          {pendingCount}
                        </Badge>
                      )}
                    </Button>
                  );
                })}
              </div>

              <Row className="g-2 align-items-end mb-3">
                {isAdmin && (
                  <Col xs={12} md={4} lg={3}>
                    <Form.Label className="small mb-1">Chi nhánh</Form.Label>
                    <Form.Select
                      value={branchId}
                      onChange={(e) => setFilters({ branch_id: e.target.value })}
                    >
                      <option value="">Tất cả chi nhánh</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </Form.Select>
                  </Col>
                )}
                <Col xs={6} md={3} lg={2}>
                  <Form.Label className="small mb-1">Từ ngày</Form.Label>
                  <Form.Control
                    type="date"
                    value={from}
                    onChange={(e) => setFilters({ from: e.target.value })}
                  />
                </Col>
                <Col xs={6} md={3} lg={2}>
                  <Form.Label className="small mb-1">Đến ngày</Form.Label>
                  <Form.Control
                    type="date"
                    value={to}
                    onChange={(e) => setFilters({ to: e.target.value })}
                  />
                </Col>
                <Col xs="auto">
                  <Button variant="outline-secondary" onClick={resetFilters}>
                    Xoá bộ lọc
                  </Button>
                </Col>
              </Row>

              {renderTable()}

              {total > PAGE_SIZE && (
                <div className="mt-3">
                  <PaginationComponent
                    totalItems={total}
                    itemsPerPage={PAGE_SIZE}
                    currentPage={page}
                    onPageChange={(p) => setFilters({ page: p })}
                    loading={loading}
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={!!selectedRow} onHide={() => setSelectedId(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            {selectedRow?.task_name || 'Chi tiết bàn giao'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{selectedRow && renderDetailBody(selectedRow)}</Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="outline-secondary" onClick={() => setSelectedId(null)}>
            Đóng
          </Button>
          {selectedRow && renderActions(selectedRow, { inModal: true })}
        </Modal.Footer>
      </Modal>

      <Modal show={!!rejectTarget} onHide={closeReject} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">Từ chối bàn giao</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label className="small">Lý do từ chối (bắt buộc)</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Nhập lý do từ chối"
            disabled={!!actingId}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeReject} disabled={!!actingId}>
            Huỷ
          </Button>
          <Button variant="danger" onClick={handleReject} disabled={!rejectReady || !!actingId}>
            {actingId ? 'Đang xử lý…' : 'Xác nhận từ chối'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!previewUrl} onHide={() => setPreviewUrl(null)} size="lg" centered>
        <Modal.Body className="text-center">
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Ảnh đính kèm phóng to"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </Modal.Body>
      </Modal>
    </PageLayout>
  );
}
